import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  PermissionEnum,
  PermissionDenied,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  Tabs,
  SidePanelLoader,
  responseErrorsResolver,
  ResponseError,
  TransactionSignatures,
  BlacklistData,
  BlacklistDataContent,
  IconAttention,
  ButtonPrimary,
  ButtonSecondary,
  SignaturePopup,
  Popup,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetTransactionsImportItemQuery,
  useLazyGetTransactionsImportItemOrdersQuery,
  useLazyGetImportItemSignaturesQuery,
  useCreateImportSignatureMutation,
  useRejectImportItemMutation,
  useLazyGetTransactionsImportHistoryQuery,
} from '../transactions/store/services';
import UploadedTransactionsGeneral from './uploaded-transactions-general';
import UploadedTransactionsOrders from './uploaded-transactions-orders';
import { clearImportTransactionSidepanelState } from './store/slices/list';
import { PaymentOrder } from '../cash-collection/types';
import {
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { MfaToken, MfaType } from '../../store/types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

let timer: NodeJS.Timeout;

const ImportItem = () => {
  const { transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'general' | 'orders' | 'signatures'
  >('general');
  const [blacklistDataVisible, setBlacklistDataVisible] =
    useState<boolean>(false);
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [errorsMessage, setErrorMessage] = useState('');

  const [getTransactionImportItem, { isLoading, isFetching }] =
    useLazyGetTransactionsImportItemQuery();
  const [
    getTransactionsImportOrders,
    { isLoading: ordersIsLoading, isFetching: ordersIsFetching },
  ] = useLazyGetTransactionsImportItemOrdersQuery();
  const [
    getImportItemSignatures,
    { isLoading: signaturesIsLoading, isFetching: signaturesIsFetching },
  ] = useLazyGetImportItemSignaturesQuery();
  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [
    createSignature,
    {
      isLoading: createSignatureInProgress,
      isSuccess: createSignatureIsSuccess,
    },
  ] = useCreateImportSignatureMutation();
  const [getTransactionsImportHistory] =
    useLazyGetTransactionsImportHistoryQuery();

  const [
    rejectImportItem,
    { isLoading: rejectInProgress, isSuccess: rejectIsSuccess },
  ] = useRejectImportItemMutation();

  const {
    transactionsDictionaries: {
      activePermissions,
      paymentTypes,
      countriesList,
      currenciesList,
    },
    import: {
      list: { importItem, orders, signatures, takeCount, ordersErrorsList },
    },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        // eslint-disable-next-line react-hooks/purity
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          id: transactionId as string,
          body: {
            mfaToken: response.data.data.token,
            signedAt: new Date().toISOString(),
          },
        })) as {
          error: { data: { message: string }; status: number };
        };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            responseErrorsResolver({
              errors: createResponse.error as ResponseError,
              setError: () => {},
              setGlobalError: setErrorMessageSignature,
            });
          }
        } else {
          setSignaturePopupVisible(false);
          setErrorMessageSignature('');
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 403) {
          setErrorMessageSignature('Access denied');
        } else if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError: () => {},
            setGlobalError: setErrorMessageSignature,
          });
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const getImportItemAction = useCallback((id: string) => {
    getTransactionImportItem(id);
    getImportItemSignatures(id);
  }, []);

  const onReject = async () => {
    const response = (await rejectImportItem({
      id: transactionId as string,
      body: {
        status: 'REJECTED',
      },
    })) as { data?: PaymentOrder; error?: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setErrorMessage,
      });
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    dispatch(clearImportTransactionSidepanelState());
    setActiveTab('general');

    setTimeout(() => {
      navigate(`${ROUTES.IMPORT}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    if (activeTab === 'orders' && transactionId) {
      getTransactionsImportOrders(transactionId);
    }
  }, [transactionId, activeTab]);

  useEffect(() => {
    if (transactionId) {
      setPanelIsOpen(true);
      getImportItemAction(transactionId);
    }
  }, [transactionId]);

  useEffect(() => {
    if (createSignatureIsSuccess && transactionId) {
      setSignaturePopupVisible(false);
      getImportItemAction(transactionId);
      getTransactionsImportHistory(
        `?take=${takeCount}&type=BATCH_IMPORT_INCOMING_TRANSACTIONS`,
      );
    }
  }, [createSignatureIsSuccess, transactionId]);

  useEffect(() => {
    if (rejectIsSuccess && transactionId) {
      setRejectPopupVisible(false);
      getImportItemAction(transactionId);
    }
  }, [rejectIsSuccess, transactionId]);

  let sidepanelContent;

  if (activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST)) {
    if (isLoading || isFetching) {
      sidepanelContent = <SidePanelLoader />;
    } else {
      sidepanelContent = (
        <div>
          {importItem &&
            !signatures?.length &&
            !importItem?.status.includes('REJ') && (
              <div className={cn.notSignedAlert}>
                <div className={cn.notSignedText}>
                  <IconAttention width={24} height={24} />
                  Additional signatures required
                </div>
                <div className={cn.notSignedControls}>
                  <ButtonPrimary
                    text={sendCodeInProgress ? 'Loading...' : 'Sign'}
                    onClick={onSendCode}
                    className={cn.signButton}
                    disabled={sendCodeInProgress}
                  />
                  <ButtonSecondary
                    text="Reject"
                    onClick={() => setRejectPopupVisible(true)}
                  />
                </div>
              </div>
            )}
          <Tabs
            tabs={[
              {
                name: 'General',
                value: 'general',
              },
              {
                name: 'Orders',
                value: 'orders',
              },
              {
                name: 'Signatures',
                value: 'signatures',
              },
            ]}
            activeTab={activeTab}
            onClick={(tab) =>
              setActiveTab(tab as 'general' | 'orders' | 'signatures')
            }
          />
          {activeTab === 'general' && (
            <UploadedTransactionsGeneral importItem={importItem} />
          )}
          {activeTab === 'orders' && !ordersIsFetching && !ordersIsLoading && (
            <UploadedTransactionsOrders
              list={orders}
              paymentTypes={paymentTypes}
              countries={countriesList}
              currenciesList={currenciesList}
              ordersErrorsList={ordersErrorsList}
            />
          )}
          {activeTab === 'signatures' &&
            !signaturesIsLoading &&
            !signaturesIsFetching && (
              <TransactionSignatures signatures={signatures} />
            )}
          {activeTab === 'orders' && (ordersIsLoading || ordersIsFetching) && (
            <SidePanelLoader className={cn.ordersLoader} />
          )}
          {activeTab === 'signatures' &&
            (signaturesIsLoading || signaturesIsFetching) && (
              <SidePanelLoader className={cn.ordersLoader} />
            )}
        </div>
      );
    }
  } else {
    sidepanelContent = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title="Incoming transaction"
        footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
        errorSidepanel={errorsMessage}
        sidepanelExpandedKey="import-transaction-panel"
      >
        {sidepanelContent}
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        sidepanelExpandedKey="import-transaction-blacklist"
        onClose={() => {
          setBlacklistDataVisible(false);
          setBlacklistData(undefined);
          setPanelIsOpen(true);
        }}
        title={blacklistData?.fullName as string}
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataVisible(false);
              setBlacklistData(undefined);
              setPanelIsOpen(true);
            }}
            bold
          />
        }
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SignaturePopup
        signaturePopupVisible={signaturePopupVisible}
        validateCode={validateCodeAction}
        validateCodeInProgress={
          validateCodeInProgress || createSignatureInProgress
        }
        errorMessageSignature={errorMessageSignature}
        onSignatureCancelClick={() => {
          setSignaturePopupVisible(false);
          clearTimeout(timer);
        }}
      />
      <Popup
        title="The transaction will be rejected"
        onCancelClick={() => setRejectPopupVisible(false)}
        onOkClick={onReject}
        cancelButtonText="Cancel"
        okButtonText="Yes, Reject"
        isVisible={rejectPopupVisible}
        parent={document.getElementById('signature-popup') as HTMLElement}
        isLoading={rejectInProgress}
      />
    </>
  );
};

export default ImportItem;
